import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../authentication/authentication/authentication.service';
import { WarehouseService } from 'src/app/services/warehouse.service';
import { WarehouseAdministrativeService } from 'src/app/services/warehouse-administrative.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { UserSaved } from 'src/app/models/user.model';
import { forkJoin } from 'rxjs';
import { ParticularityInvoiceRoutingServiceService } from 'src/app/services/particularity-invoice-routing-service.service';

type Id = number | null;

interface WarehouseRow {
  id: number;
  code: string;
  name: string;
  address?: string;
  companyId: number;
  companyName: string;
  // Selecciones de esta pantalla:
  invoiceWarehouseId: Id; // fiscal para factura
  cfWarehouseId: Id;      // fiscal para CF/Efectivo
  auxiliarWarehouseId: Id;  // fiscal auxiliar
  auxiliarLimit: number;  // limite para fiscal auxiliar
  percentageTwo: number; // porcentaje a cotizar
  // Estado:
  saving?: boolean;
}

interface FiscalWarehouse {
  id: number;
  code: string;
  name: string;
  companyId: number;
  companyName: string;
}

@Component({
  selector: 'app-particularity-invoice-routing',
  templateUrl: './particularity-invoice-routing.component.html',
  styleUrls: ['./particularity-invoice-routing.component.scss']
})
export class ParticularityInvoiceRoutingComponent {
  user: UserSaved = null;

  isAdministrative = false;
  adminCompanyId = null;

  warehousesAdministrative: WarehouseRow[] = [];
  warehousesFiscales: FiscalWarehouse[] = [];

  // OPCIONAL: si true evita repetir una misma fiscal entre filas
  preventGlobalRepeats = false;


  constructor(
    private authService: AuthenticationService,
    private warehouseService: WarehouseService,
    private warehouseAdministrativeService: WarehouseAdministrativeService,
    private routingService: ParticularityInvoiceRoutingServiceService,
    private toast: ToastrService,
  ) {
    this.isAdministrative = JSON.parse(this.authService.getCurrentCompany()).administrative;
    this.adminCompanyId = this.authService.getCurrentCompanyId();
    this.user = this.authService.getUser();

    if (this.isAdministrative) {
      this.loadStartData();
    }
  }

  // ---- Carga inicial ----
  loadStartData(): void {
    // 1) Cargar bodegas admin
    this.getWarehousesAdmin(() => {
      // 2) Cargar bodegas fiscales
      this.getWarehousesFiscales(() => {
        // 3) Prefetch de rutas ya guardadas (si existen) para precargar selects
        this.prefillExistingRoutes();
      });
    });
  }

  getWarehousesAdmin(done?: () => void): void {
    this.warehouseService.getWarehouses().subscribe({
      next: (resp: ResponseObject) => {
        const rows = (resp.data || []).filter(w => w.name !== 'Bodega por defecto');
        this.warehousesAdministrative = rows.map((w: any) => ({
          id: w.id,
          code: w.code,
          name: w.name,
          address: w.address,
          companyId: w.companyId ?? this.adminCompanyId,
          companyName: w.companyName ?? (JSON.parse(this.authService.getCurrentCompany()).name || ''),

          invoiceWarehouseId: null,
          cfWarehouseId: null,

          auxiliarWarehouseId: null,
          auxiliarLimit: null,

          percentageTwo: 0
        }));
        done?.();
      },
      error: (err: HttpErrorResponse) => {
        this.toast.error(err.error?.message || 'Error al obtener las bodegas administrativas');
      }
    });
  }

  getWarehousesFiscales(done?: () => void): void {
    // Trae TODAS las bodegas fiscales relacionadas a esta empresa administrativa
    this.warehouseAdministrativeService.getAllFiscalWarehouses(this.adminCompanyId).subscribe({
      next: (resp: ResponseObject) => {
        this.warehousesFiscales = (resp.data || [])
          .filter((w: any) => w.name !== 'Bodega por defecto')
          .map((w: any) => ({
            id: w.id,
            code: w.code,
            name: w.name,
            companyId: w.companyId,
            companyName: w.companyName,
          }));
        done?.();
      },
      error: (err: HttpErrorResponse) => {
        this.toast.error(err.error?.message || 'Error al obtener las bodegas fiscales');
      }
    });
  }

  prefillExistingRoutes(): void {
    this.routingService.getRoutesByAdminCompany(this.adminCompanyId).subscribe({
      next: (resp: ResponseObject) => {
        const routes = resp.data || [];
        // routes esperados: [{ adminWarehouseId, invoiceWarehouse, cfWarehouse }, ...]
        const byAdminId = new Map<number, any>();
        routes.forEach((r: any) => byAdminId.set(r.adminWarehouse, r));
        console.log(resp.data);
        this.warehousesAdministrative = this.warehousesAdministrative.map(row => {
          const found = byAdminId.get(row.id);

          return found ? {
            ...row,

            invoiceWarehouseId: found.invoiceWarehouse ?? null,
            cfWarehouseId: found.cfWarehouse ?? null,

            auxiliarWarehouseId: found.auxiliarWarehouse ?? null,
            auxiliarLimit: found.auxiliarLimit ?? null,

            percentageTwo: found.percentageTwo ?? 0,
          } : row;
        });
      },
      error: () => {
        // No bloquear si falla la precarga; solo informar suave
        // this.toast.info('No se pudieron precargar rutas existentes');
      }
    });
  }

  // ---- UI helpers ----

  onSelectChanged(row: WarehouseRow, kind: 'invoice' | 'cf'): void {
    // No permitir que ambas seleccionen la misma fiscal en la MISMA fila
    if (row.invoiceWarehouseId && row.cfWarehouseId && row.invoiceWarehouseId === row.cfWarehouseId) {
      if (kind === 'invoice') row.invoiceWarehouseId = null;
      else row.cfWarehouseId = null;
      this.toast.warning('No puedes repetir la misma bodega fiscal en Factura y CF/Efectivo para la misma bodega administrativa.');
      return;
    }
  }

  // (Opcional) Evitar repetidos entre filas — usado por el template para deshabilitar opciones
  isUsedGlobally(kind: 'invoice' | 'cf', fiscalId: number, rowIndex: number): boolean {
    if (!this.preventGlobalRepeats || !fiscalId) return false;
    for (let i = 0; i < this.warehousesAdministrative.length; i++) {
      if (i === rowIndex) continue;
      const r = this.warehousesAdministrative[i];
      if (kind === 'invoice' && r.invoiceWarehouseId === fiscalId) return true;
      if (kind === 'cf' && r.cfWarehouseId === fiscalId) return true;
    }
    return false;
  }

  canSaveAll(): boolean {
    // Permitir guardar si:
    // - No hay filas inválidas (mismas fiscales por fila)
    // - Y todas las filas "iniciadas" están completas (si marcó una, debe marcar la otra)
    for (const r of this.warehousesAdministrative) {
      if (r.invoiceWarehouseId && r.cfWarehouseId && r.invoiceWarehouseId === r.cfWarehouseId) return false;
      if ((r.invoiceWarehouseId && !r.cfWarehouseId) || (!r.invoiceWarehouseId && r.cfWarehouseId)) return false;
    }
    // Si ninguna fila tiene selección, también permitimos (el backend decidirá qué hacer)
    return true;
  }

  reloadData(): void {
    this.loadStartData();
  }

  // ---- Guardado ----

  // Guarda SOLO la fila
  saveRow(row: WarehouseRow): void {
    if (!row.invoiceWarehouseId || !row.cfWarehouseId) {
      //this.toast.warning('Selecciona ambas bodegas fiscales (Factura y CF/Efectivo) antes de guardar.');
      //return;
      row.invoiceWarehouseId = null;
      row.cfWarehouseId = null;
      row.percentageTwo = null;
    } else {
      if (row.invoiceWarehouseId === row.cfWarehouseId) {
        this.toast.error('No puedes usar la misma bodega fiscal para Factura y CF/Efectivo.');
        return;
      }
    }

    const payload = {
      companyAdminId: Number.parseInt(this.adminCompanyId),
      warehouseAdminId: row.id,

      invoiceWarehouse: row.invoiceWarehouseId,
      cfWarehouse: row.cfWarehouseId,

      auxiliarWarehouse: row.auxiliarWarehouseId,
      auxiliarLimit: row.auxiliarLimit,

      percentageTwo: row.percentageTwo
    };

    row.saving = true;
    this.routingService.saveRoute(payload).subscribe({
      next: () => {
        row.saving = false;
        this.toast.success(`Ruta guardada para "${row.name}".`);
      },
      error: (err: HttpErrorResponse) => {
        row.saving = false;
        this.toast.error(err.error?.message || `Error al guardar la ruta de "${row.name}".`);
      }
    });
  }

  // Guarda TODAS las filas con selección completa
  saveAll(): void {
    if (!this.canSaveAll()) {
      this.toast.error('Revisa que cada fila tenga ambas fiscales seleccionadas y que no se repitan en la misma fila.');
      return;
    }

    const toSave = this.warehousesAdministrative
      .filter(r => r.invoiceWarehouseId && r.cfWarehouseId)
      .map(r => ({
        adminCompany: this.adminCompanyId,
        adminWarehouse: r.id,
        invoiceWarehouse: r.invoiceWarehouseId,
        cfWarehouse: r.cfWarehouseId,
        percentageTwo: r.percentageTwo,
      }));

    if (toSave.length === 0) {
      this.toast.info('No hay filas para guardar.');
      return;
    }

    // Marcar todas como "saving"
    this.warehousesAdministrative.forEach(r => {
      if (r.invoiceWarehouseId && r.cfWarehouseId) r.saving = true;
    });
    forkJoin(toSave.map(p => this.routingService.saveRoute(p))).subscribe({
      next: () => {
        this.warehousesAdministrative.forEach(r => r.saving = false);
        this.toast.success('Rutas guardadas correctamente.');
        // Opcional: recargar para refrescar del backend
        // this.reloadData();
      },
      error: (err: HttpErrorResponse) => {
        this.warehousesAdministrative.forEach(r => r.saving = false);
        this.toast.error(err.error?.message || 'Error al guardar una o más rutas.');
      }
    });
  }
}
